import React from 'react';
import { useUniversalTextSizing, getUniversalTextConfig } from '../utils/cardTitleSizing';

/**
 * Universal Text Component
 * 
 * A component that automatically applies consistent text sizing based on the global
 * text sizing system. Use this component for consistent typography across the judge station.
 */

const UniversalText = ({ 
  variant = 'bodyText', 
  as: Component = 'span',
  className = '',
  children,
  ...props 
}) => {
  const { textSize } = useUniversalTextSizing();
  const textConfig = getUniversalTextConfig(textSize);
  
  const variantClass = textConfig[variant] || textConfig.bodyText;
  
  return (
    <Component 
      className={`${variantClass} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * Specialized text components for common use cases
 */

export const CardTitle = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="cardTitle" 
    as="h3" 
    className={`font-black text-white uppercase tracking-wide ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const CardSubtitle = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="cardSubtitle" 
    as="h4" 
    className={`font-medium text-white/80 ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const BodyText = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="bodyText" 
    className={`text-white/90 ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const SmallText = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="smallText" 
    className={`text-white/70 ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const CaptionText = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="captionText" 
    className={`text-white/60 ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const ButtonText = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="buttonText" 
    className={`font-medium ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const LabelText = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="labelText" 
    as="label"
    className={`font-medium text-white/80 ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const ScoreDisplay = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="scoreDisplay" 
    className={`font-black leading-none ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

export const ScoreLabel = ({ className = '', children, ...props }) => (
  <UniversalText 
    variant="scoreLabel" 
    className={`text-white/60 ${className}`}
    {...props}
  >
    {children}
  </UniversalText>
);

/**
 * Universal Button Component with consistent sizing
 */
export const UniversalButton = ({ 
  variant = 'primary',
  size = 'auto', // 'auto' uses universal sizing, or override with 'small', 'medium', 'large'
  className = '',
  children,
  ...props 
}) => {
  const { textSize } = useUniversalTextSizing();
  const textConfig = getUniversalTextConfig(textSize);
  
  const sizeClass = size === 'auto' 
    ? `${textConfig.buttonHeight} ${textConfig.buttonPadding} ${textConfig.buttonText}`
    : size === 'small' 
    ? 'h-8 px-2 py-1 text-xs'
    : size === 'large'
    ? 'h-12 px-4 py-2 text-base'
    : 'h-10 px-3 py-1.5 text-sm';
  
  const variantClass = variant === 'primary'
    ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50 text-white hover:from-purple-500/40 hover:to-pink-500/40'
    : variant === 'secondary'
    ? 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
    : variant === 'success'
    ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-100 hover:from-green-500/40 hover:to-emerald-500/40'
    : variant === 'danger'
    ? 'bg-gradient-to-r from-red-500/30 to-pink-500/30 border border-red-400/50 text-red-100 hover:from-red-500/40 hover:to-pink-500/40'
    : 'bg-white/10 border border-white/20 text-white hover:bg-white/20';
  
  return (
    <button 
      className={`
        ${sizeClass}
        ${variantClass}
        rounded-xl font-medium transition-all duration-300 backdrop-blur-xl
        flex items-center justify-center gap-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Universal Input Component with consistent sizing
 */
export const UniversalInput = ({ 
  className = '',
  ...props 
}) => {
  const { textSize } = useUniversalTextSizing();
  const textConfig = getUniversalTextConfig(textSize);
  
  return (
    <input 
      className={`
        ${textConfig.inputHeight} ${textConfig.inputText} ${textConfig.sectionPadding}
        w-full rounded-xl border border-white/20 bg-white/10 text-white placeholder-white/40
        focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20
        backdrop-blur-xl transition-colors
        ${className}
      `}
      {...props}
    />
  );
};

/**
 * Universal Badge Component with consistent sizing
 */
export const UniversalBadge = ({ 
  variant = 'default',
  className = '',
  children,
  ...props 
}) => {
  const { textSize } = useUniversalTextSizing();
  const textConfig = getUniversalTextConfig(textSize);
  
  const variantClass = variant === 'success'
    ? 'bg-green-500/20 border-green-400/40 text-green-200'
    : variant === 'warning'
    ? 'bg-yellow-500/20 border-yellow-400/40 text-yellow-200'
    : variant === 'danger'
    ? 'bg-red-500/20 border-red-400/40 text-red-200'
    : variant === 'info'
    ? 'bg-blue-500/20 border-blue-400/40 text-blue-200'
    : 'bg-white/10 border-white/20 text-white/80';
  
  return (
    <span 
      className={`
        ${textConfig.badgeSize}
        ${variantClass}
        rounded-full border backdrop-blur-xl font-medium
        ${className}
      `}
      {...props}
    >
      {children}
    </span>
  );
};

/**
 * Hook to get universal sizing configuration
 */
export const useUniversalSizing = () => {
  const { textSize } = useUniversalTextSizing();
  return getUniversalTextConfig(textSize);
};

export default UniversalText;
